'use client';
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from 'tailwind-variants';
import { vimeoPlayerVariants } from './VimeoPlayer.styles';
import { trackAdobeVideoPlay } from '@/utils/adobe';

export interface VimeoPlayerProps {
  /** Vimeo video ID (e.g. "123456789") */
  videoId?: string;
  /** Accessible title for the iframe */
  title?: string;
  /** Allow autoplay within the embed */
  autoplay?: boolean;
  /** Allow autopause within the embed */
  autopause?: boolean;
  /** Mute the video playing in embed */
  muted?: boolean;
  /** Show controls withing the embed */
  controls?: boolean;
  /** Loop the video */
  loop?: boolean;
  /** Show the video title overlay */
  showTitle?: boolean;
  /** Show the byline (author info) */
  showByline?: boolean;
  /** Show the portrait (author avatar) */
  showPortrait?: boolean;
  /** Additional class for the wrapper */
  className?: string;
  /** Size the wrapper to the video's real aspect ratio (fetched from Vimeo) instead of a fixed one */
  responsiveAspectRatio?: boolean;
  /** Fire the Adobe "Video Start" event on play; set false for decorative/non-interactive videos */
  trackPlay?: boolean;
  // Video Parameters
  videoParameters?: string;
}

// Default 16:9 ratio used before the real ratio resolves — prevents the absolutely-positioned iframe from collapsing
const DEFAULT_ASPECT_RATIO = '16 / 9';

// Fetches the video's true width/height from Vimeo's public oEmbed endpoint and returns an aspect-ratio string
const fetchVimeoAspectRatio = async (videoId: string): Promise<string | null> => {
  try {
    const endpoint = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${videoId}`)}`;
    const res = await fetch(endpoint);
    if (!res.ok) return null;
    const data = (await res.json()) as { width?: number; height?: number };
    if (!data.width || !data.height) return null;
    return `${data.width} / ${data.height}`;
  } catch {
    return null;
  }
};

// Parsed shape of a Vimeo postMessage payload
type VimeoMessage = {
  event?: string;
  player_id?: string;
};

// Safely parses a raw MessageEvent payload into a VimeoMessage
const parseVimeoMessage = (raw: unknown): VimeoMessage | null => {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (data && typeof data === 'object') return data as VimeoMessage;
  } catch {
    return null;
  }
  return null;
};

// Params that authored values must never override — player_id links postMessages back to this instance and dnt keeps Vimeo tracking off
const PROTECTED_VIMEO_PARAMS = ['player_id', 'dnt'];

// Merges an authored Vimeo query string (e.g. "?autoplay=1&muted=0" or a pasted embed URL) into the built params
const applyAuthoredParameters = (params: URLSearchParams, videoParameters: string): void => {
  const trimmed = videoParameters.trim();
  if (!trimmed) return;

  // Tolerate a full URL, a leading "?"/"&", or a bare "key=value&key=value" string; any hash fragment is dropped
  const withoutFragment = trimmed.split('#')[0];
  const queryString = withoutFragment.includes('?')
    ? withoutFragment.slice(withoutFragment.indexOf('?') + 1)
    : withoutFragment.replace(/^&+/, '');

  new URLSearchParams(queryString).forEach((value, key) => {
    // Skip protected keys and malformed entries authored without a value
    if (!key || !value || PROTECTED_VIMEO_PARAMS.includes(key)) return;
    params.set(key, value);
  });
};

// Builds the Vimeo embed URL; playerId tags the iframe so postMessages can be matched back to this instance
const buildVimeoUrl = (props: VimeoPlayerProps, playerId: string): string => {
  const {
    videoId,
    autoplay = false,
    autopause = false,
    muted = true,
    controls = true,
    loop = false,
    showTitle = false,
    showByline = false,
    showPortrait = false,
    videoParameters = '',
  } = props;

  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    autopause: autopause ? '1' : '0',
    muted: muted ? '1' : '0',
    controls: controls ? '1' : '0',
    loop: loop ? '1' : '0',
    title: showTitle ? '1' : '0',
    byline: showByline ? '1' : '0',
    portrait: showPortrait ? '1' : '0',
    dnt: '1',
    player_id: playerId,
  });

  applyAuthoredParameters(params, videoParameters);

  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
};

const VimeoPlayer = forwardRef<HTMLIFrameElement, VimeoPlayerProps>(
  (
    {
      videoId,
      title = 'Vimeo video player',
      autoplay = false,
      autopause = false,
      muted = autoplay ? true : false,
      controls = true,
      loop = false,
      showTitle = false,
      showByline = false,
      showPortrait = false,
      className,
      responsiveAspectRatio = false,
      trackPlay = true,
      videoParameters,
      ...rest
    },
    ref
  ) => {
    const { wrapper, iframe } = vimeoPlayerVariants();

    // Holds the video's real aspect ratio once resolved; only used when responsiveAspectRatio is enabled
    const [aspectRatio, setAspectRatio] = useState<string>(DEFAULT_ASPECT_RATIO);

    // Stable unique ID scoped to this component instance — used as Vimeo player_id
    const rawId = useId();
    const playerId = rawId.replace(/[^a-zA-Z0-9]/g, '');

    // Internal ref used to post subscription messages back to the Vimeo iframe
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Merges the internal ref with the forwarded ref so both stay in sync
    const setRef = useCallback(
      (node: HTMLIFrameElement | null) => {
        iframeRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.RefObject<HTMLIFrameElement | null>).current = node;
        }
      },
      [ref]
    );

    // Resolve the video's real aspect ratio so the wrapper height follows its width without letterboxing
    useEffect(() => {
      if (!responsiveAspectRatio || !videoId) return;

      let active = true;
      fetchVimeoAspectRatio(videoId).then((ratio) => {
        if (active && ratio) setAspectRatio(ratio);
      });
      return () => {
        active = false;
      };
    }, [responsiveAspectRatio, videoId]);

    useEffect(() => {
      if (!videoId) return;

      const handleMessage = (e: MessageEvent) => {
        // Ignore messages from non-Vimeo origins
        if (!e.origin.includes('vimeo.com')) return;

        const msg = parseVimeoMessage(e.data);
        if (!msg) return;

        // Ignore messages from other Vimeo player instances on the same page
        if (msg.player_id !== playerId) return;

        if (msg.event === 'ready') {
          // Subscribe to the play event now that the player is ready
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: 'addEventListener', value: 'play', player_id: playerId }),
            'https://player.vimeo.com'
          );
        }

        if (msg.event === 'play' && trackPlay) {
          // trackAdobeVideoPlay is a no-op after the first call for this videoName
          trackAdobeVideoPlay({ videoName: title });
        }
      };

      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, [videoId, title, playerId, trackPlay]);

    if (!videoId) return <></>;

    const src = buildVimeoUrl(
      {
        videoId,
        autoplay,
        autopause,
        muted,
        controls,
        loop,
        showTitle,
        showByline,
        showPortrait,
        videoParameters,
      },
      playerId
    );

    return (
      <div
        className={wrapper({ class: className })}
        data-component="helpers/wrappers/vimeoplayer"
        style={responsiveAspectRatio ? { aspectRatio } : undefined}
        {...rest}
      >
        <iframe
          ref={setRef}
          src={src}
          title={title}
          className={cn(iframe(), 'vimeo-player')}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
    );
  }
);

VimeoPlayer.displayName = 'VimeoPlayer';

export default VimeoPlayer;
